import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
  Navigate
} from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Eye,
  Timer,
  Gem,
  Trophy,
  BarChart2,
  DollarSign,
  X
} from "lucide-react";

import "./App.css";

import { useAuth } from "./context/AuthContext";
import DocumentTitle from "./components/DocumentTitle";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import { getPushPermission, requestPushPermission } from "./services/notificationService";
import { registerFcmToken } from "./services/fcmService";
import { markPushPromptDismissed, shouldShowPushPrompt } from "./utils/pushPrompt";
import { applyTheme } from "./services/userPerksService";
import { fetchUserByHandle } from "./services/userLookupService";
import { getUserProfilePath } from "./utils/profilePaths";

// ── Legal pages (new) ─────────────────────────────────────────────────────────
import PrivacyPolicy  from "./components/legal/PrivacyPolicy";
import TermsOfServicePage from "./components/legal/TermsOfService";
import Unsubscribe    from "./components/legal/Unsubscribe";

// ── Screens ───────────────────────────────────────────────────────────────────
import Home              from "./screens/Home";
import FollowingFeed     from "./screens/FollowingFeed";
import Discover          from "./screens/Discover";
import FreshMomentsPage  from "./screens/FreshMomentsPage";
import RisingPage        from "./screens/RisingPage";
import DropView          from "./pages/DropView";
import CreatorProfile    from "./pages/creator/CreatorProfile";
import ProfileSettings    from "./pages/creator/ProfileSettings";
import FanProfile from "./pages/fan/FanProfile";
import FanProfileEdit from "./pages/fan/FanProfileEdit";
import { OwnUserProfile, OtherUserProfile } from "./screens/UserProfile";
import UserProfileEdit   from "./screens/UserProfileEdit";
import Notifications     from "./screens/Notifications";
import SettingsPage      from "./pages/Settings";
import Dashboard         from "./pages/creator/Dashboard";
import MyDrops           from "./pages/creator/MyDrops";
import PostADrop         from "./pages/PostADrop";
import ScheduleDrop      from "./pages/creator/ScheduleDrop";
import SubscriptionTiers from "./pages/creator/SubscriptionTiers";
import CreatorOnboarding from "./screens/CreatorOnboarding";
import Broadcasts        from "./screens/Broadcasts";
import Earnings          from "./pages/creator/Earnings";
import Subscribers       from "./pages/creator/Subscribers";
import Analytics         from "./pages/creator/Analytics";
import AppreciationWall    from "./pages/creator/AppreciationWall";
import ThemeCatalog        from "./pages/creator/ThemeCatalog";
import Search              from "./pages/Search";
import Login             from "./screens/Login";
import Signup            from "./screens/Signup";
import LandingPage       from "./screens/LandingPage";
import WaitlistFan         from "./screens/WaitlistFan";
import WaitlistCreator     from "./screens/WaitlistCreator";
import BecomeCreator     from "./screens/BecomeCreator";
import Referral          from "./screens/Referral";
import ReferralLanding   from "./screens/ReferralLanding";
import { ReferralOverlayProvider } from "./context/ReferralOverlayContext";
import Help              from "./screens/Help";
import Legal             from "./screens/Legal";
import AdminReports      from "./screens/AdminReports";
import BetaChecklist     from "./screens/BetaChecklist";
import Perks             from "./screens/Perks";
import Games             from "./screens/Games";
import WordDropLeaderboard from "./screens/WordDropLeaderboard";
import NumberDropLeaderboard from "./screens/NumberDropLeaderboard";
import FeaturedLanding   from "./pages/featured/FeaturedLanding";
import {
  FeaturedDiscoveryPage,
  FeaturedCommunityPage,
} from "./pages/featured/FeaturedCategoryPage";

// ── Components ────────────────────────────────────────────────────────────────
import CreatorOnly           from "./components/CreatorOnly";
import SignedInOnly          from "./components/SignedInOnly";
import EmailVerificationGate from "./components/EmailVerificationGate";
import Nav                     from "./components/Nav";
import AddToHomeScreen, { requestA2hsResurface } from "./components/AddToHomeScreen";
import { PipProvider, PipEgg, PipOnboarding } from "./components/Pip";
import LoginOverlay          from "./components/LoginOverlay";
import NotFound              from "./screens/NotFound";
import PipMasters            from "./pages/PipMasters";
import AdminGate             from "./components/AdminGate";
import AdminHome             from "./pages/admin/AdminHome";
import AdminCreatorApprovals from "./pages/admin/AdminCreatorApprovals";
import AdminSupportInbox     from "./pages/admin/AdminSupportInbox";
import AdminGames            from "./pages/admin/AdminGames";
import AdminOutreach         from "./pages/admin/AdminOutreach";
import AdminEmailTemplates   from "./pages/admin/AdminEmailTemplates";
import AdminFanWaitlist      from "./pages/admin/AdminFanWaitlist";
import AdminUsers            from "./pages/admin/AdminUsers";
import AdminSettings         from "./pages/admin/AdminSettings";
import AdminCommentReports   from "./pages/admin/AdminCommentReports";
import AdminSuggestions      from "./pages/admin/AdminSuggestions";
import SuggestionBox         from "./components/SuggestionBox";

/* =========================================
   BECOME CREATOR MODAL
========================================= */

function BecomeCreatorModal({ onClose }) {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const handleBecomeCreator = async () => {
    if (!currentUser) {
      onClose();
      navigate("/welcome?auth=signin");
      return;
    }
    try {
      await updateDoc(doc(db, "users", currentUser.uid), {
        isCreator: true,
        updatedAt: serverTimestamp()
      });
      navigate("/creator-onboarding");
    } catch (error) {
      console.error("Become creator error:", error);
    }
  };

  const features = [
    { icon: <Eye size={20} strokeWidth={2} />,      color: "#8b5cf6", bg: "rgba(139,92,246,0.15)",  title: "Limited moments",   desc: "Post content that starts free then becomes supporter-only after the view window ends." },
    { icon: <Timer size={20} strokeWidth={2} />,    color: "#f59e0b", bg: "rgba(245,158,11,0.15)",  title: "Drop mode",         desc: "Schedule releases, build hype, and create urgency around upcoming content." },
    { icon: <Gem size={20} strokeWidth={2} />,      color: "#06b6d4", bg: "rgba(6,182,212,0.15)",   title: "Support tiers",     desc: "Build premium supporter levels and earn recurring revenue from your audience." },
    { icon: <Trophy size={20} strokeWidth={2} />,   color: "#f97316", bg: "rgba(249,115,22,0.15)",  title: "User perks",        desc: "Reward supporters with badges, early access, and exclusive perk unlocks." },
    { icon: <BarChart2 size={20} strokeWidth={2} />,color: "#22c55e", bg: "rgba(34,197,94,0.15)",   title: "Analytics",         desc: "Track views, earnings, supporters, and engagement from one clean dashboard." },
    { icon: <DollarSign size={20} strokeWidth={2} />,color:"#ef4444", bg: "rgba(239,68,68,0.15)",   title: "Canadian payouts",  desc: "Get paid in CAD with transparent fees — built specifically for Canadian creators." },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card become-creator-card" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}><X size={20} strokeWidth={2.5} /></button>
        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />

        <div className="auth-card-header">
          <div className="auth-badge">🎬 Creator Mode</div>
          <h1>Turn your profile into a creator storefront</h1>
          <p>Unlock moments, drops, support tiers, analytics, and Canadian creator payouts.</p>
        </div>

        <div className="creator-feature-grid">
          {features.map((f) => (
            <div className="creator-feature-card" key={f.title}>
              <div className="creator-feature-icon" style={{ background: f.bg, color: f.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {f.icon}
              </div>
              <strong>{f.title}</strong>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="become-creator-actions">
          <button type="button" className="auth-submit" onClick={handleBecomeCreator}>
            {userProfile?.isCreator ? "Continue setup" : "Start creator setup"}
          </button>
          <button
            type="button"
            className="become-creator-explore"
            onClick={onClose}
            style={{
              height: "52px", padding: "0 24px", borderRadius: "14px",
              border: "1.5px solid rgba(255,255,255,0.2)",
              background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.75)",
              fontSize: "15px", fontWeight: 700, cursor: "pointer", transition: "all 0.15s ease",
            }}
          >
            Explore creators first
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================
   APP LAYOUT
========================================= */

/** Legacy post URLs share the same drop IDs as /drop/:id */
function PostToDropRedirect() {
  const { id } = useParams();
  return <Navigate to={`/drop/${encodeURIComponent(id || "")}`} replace />;
}

/** Legacy /profile/:username → /creator/:handle or /fan/:uid */
function ProfileUsernameRedirect() {
  const { username } = useParams();
  const [dest, setDest] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const raw = (username || "").trim();
    if (!raw) {
      setMissing(true);
      return undefined;
    }

    setDest(null);
    setMissing(false);

    fetchUserByHandle(raw)
      .then((user) => {
        if (cancelled) return;
        const path = getUserProfilePath(user);
        if (path && path !== `/profile/${raw}`) {
          setDest(path);
          return;
        }
        setMissing(true);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });

    return () => {
      cancelled = true;
    };
  }, [username]);

  if (missing) return <NotFound />;
  if (dest) return <Navigate to={dest} replace />;
  return (
    <div className="page">
      <p>Finding profile…</p>
    </div>
  );
}

function AppLayout() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { currentUser, userProfile, userPerks } = useAuth();

  const [showPushPrompt, setShowPushPrompt]     = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [a2hsBlocking, setA2hsBlocking]         = useState(false);

  const authParam = new URLSearchParams(location.search).get("auth");
  const showAuthOverlay =
    location.pathname === "/welcome" &&
    ["signin", "signup", "forgot"].includes(authParam);

  const closeAuthOverlay = () => {
    navigate("/welcome", { replace: true });
  };

  const publicPages  = [
    "/welcome",
    "/waitlist/fan",
    "/waitlist/creator",
    "/login",
    "/signup",
    "/terms",
    "/become-creator",
    "/privacy",
    "/unsubscribe",
    "/creators/join",
  ];
  const isPublicPage =
    publicPages.includes(location.pathname) || location.pathname.startsWith("/r/");
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isAuthenticated = !!currentUser;
  const isRootHome =
    location.pathname === "/" || location.pathname === "/home";
  const isSignedInOnlyRoute =
    isRootHome ||
    location.pathname === "/gallery" ||
    location.pathname.startsWith("/gallery/") ||
    location.pathname === "/discover" ||
    location.pathname.startsWith("/discover/") ||
    location.pathname === "/following" ||
    location.pathname === "/notifications" ||
    location.pathname === "/perks" ||
    location.pathname === "/featured" ||
    location.pathname.startsWith("/featured/");
  // Show nav on app routes (e.g. Home at /); hide on marketing/auth public pages and admin shell.
  // Logged-out gated routes redirect to /welcome — don't flash the signed-in nav first.
  const showNav =
    (isAuthenticated || !isPublicPage) &&
    !isAdminRoute &&
    !(isSignedInOnlyRoute && !isAuthenticated);
  const showAppShell = isAuthenticated && !isPublicPage && !isAdminRoute;

  /* ── Apply saved theme on load / when profile updates ── */
  useEffect(() => {
    const themeId = userProfile?.theme || userPerks?.theme || "warmCream";
    applyTheme(themeId);
  }, [userProfile?.theme, userPerks?.theme]);

  /* ── Push permission prompt (cooldown + Settings toggle) ── */
  useEffect(() => {
    if (!currentUser) return;
    if (a2hsBlocking) return;
    const perm = getPushPermission();
    const remindEnabled = userProfile?.notifications?.remindPushPrompt !== false;
    if (!shouldShowPushPrompt({ permission: perm, remindEnabled })) {
      setShowPushPrompt(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      // Judgment: resurface A2HS when the in-app push-permission bar would show
      // (first "about to receive pushes" client moment), not on FCM payload arrival.
      requestA2hsResurface();
      setShowPushPrompt(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [currentUser, userProfile?.notifications?.remindPushPrompt, a2hsBlocking]);

  const handlePushAccept = async () => {
    setShowPushPrompt(false);
    const permission = await requestPushPermission();
    if (permission === "granted" && currentUser?.uid) {
      try {
        await registerFcmToken(currentUser.uid);
      } catch (err) {
        console.error("FCM token registration failed:", err);
      }
    }
  };

  const handlePushDismiss = () => {
    markPushPromptDismissed();
    setShowPushPrompt(false);
  };

  useEffect(() => {
    if (currentUser && showAuthOverlay) {
      navigate("/", { replace: true });
    }
  }, [currentUser, showAuthOverlay, navigate]);

  /* ── Auth redirect helpers ── */
  useEffect(() => {
    if (!currentUser && location.pathname === "/become-creator") {
      setShowCreatorModal(true);
      navigate("/welcome", { replace: true });
    }
  }, [location.pathname, currentUser, navigate]);

  const closeCreatorModal = () => {
    setShowCreatorModal(false);
  };

  const isSettingsRoute = location.pathname === "/settings";

  const isHomeRoute =
    location.pathname === "/" || location.pathname === "/home";

  const isGalleryRoute =
    location.pathname === "/gallery" ||
    location.pathname.startsWith("/gallery/") ||
    location.pathname === "/discover" ||
    location.pathname.startsWith("/discover/");

  const isFeaturedRoute =
    location.pathname === "/featured" || location.pathname.startsWith("/featured/");

  const isFeaturedLandingRoute = location.pathname === "/featured";

  const isFeaturedCategoryRoute =
    location.pathname === "/featured/discovery" ||
    location.pathname === "/featured/community";

  const isPerksRoute = location.pathname === "/perks";

  const isSearchRoute =
    location.pathname === "/search" || location.pathname.startsWith("/search/");

  const isDropRoute =
    location.pathname.startsWith("/drop/");

  const isGamesRoute =
    location.pathname === "/games" || location.pathname.startsWith("/games/");

  const isCreatorDashboardRoute =
    [
      "/dashboard",
      "/my-drops",
      "/post",
      "/subscribers",
      "/earnings",
      "/analytics",
      "/support-tiers",
      "/appreciation-wall",
      "/broadcasts",
      "/schedule-drop",
    ].includes(location.pathname) ||
    /^\/creator\/[^/]+\/edit$/.test(location.pathname);

  const isProfileRoute =
    !isCreatorDashboardRoute &&
    (location.pathname === "/profile" ||
    location.pathname.startsWith("/profile/") ||
    location.pathname.startsWith("/creator/") ||
    location.pathname.startsWith("/user/") ||
    location.pathname.startsWith("/fan/"));

  return (
    <div className={showNav ? "app-shell" : "public-shell"}>
      <DocumentTitle />

      {showNav && <Nav />}

      {/* ── MODALS ── */}
      {showAuthOverlay && (
        <LoginOverlay initialMode={authParam} onClose={closeAuthOverlay} />
      )}
      {showCreatorModal && (
        <BecomeCreatorModal onClose={closeCreatorModal} />
      )}

      {/* ── PUSH PROMPT ── */}
      {showPushPrompt && (
        <div className="push-prompt">
          <div className="push-prompt-icon">🔔</div>
          <div className="push-prompt-text">
            <strong>Stay in the loop</strong>
            <p>Get notified when creators you follow drop exclusive content.</p>
          </div>
          <div className="push-prompt-actions">
            <button type="button" className="push-prompt-accept" onClick={handlePushAccept}>Allow</button>
            <button type="button" className="push-prompt-dismiss" onClick={handlePushDismiss}>Not now</button>
          </div>
        </div>
      )}

      <main
        className={
          showNav
            ? `app-main${isSettingsRoute ? " app-main--settings" : ""}${isHomeRoute ? " app-main--home" : ""}${isGamesRoute ? " app-main--games" : ""}${isGalleryRoute ? " app-main--gallery" : ""}${isFeaturedLandingRoute ? " app-main--featured-landing" : ""}${isFeaturedCategoryRoute ? " app-main--featured-category" : ""}${isFeaturedRoute && !isFeaturedLandingRoute && !isFeaturedCategoryRoute ? " app-main--featured" : ""}${isPerksRoute ? " app-main--perks" : ""}${isSearchRoute ? " app-main--search" : ""}${isDropRoute ? " app-main--drop" : ""}${isProfileRoute ? " app-main--profile" : ""}${isCreatorDashboardRoute ? " app-main--post-drop" : ""}`
            : "landing-page-content"
        }
      >
        <Routes>

          {/* ── PUBLIC / PRE-LAUNCH ── */}
          <Route path="/" element={<SignedInOnly><Home /></SignedInOnly>} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/waitlist/fan" element={<WaitlistFan />} />
          <Route path="/waitlist/creator" element={<WaitlistCreator />} />
          <Route path="/r/:code" element={<ReferralLanding />} />
          <Route path="/creators/join" element={<ReferralLanding joinAs="creator" />} />
          <Route path="/login"          element={<Login />} />
          <Route path="/signup"         element={<Signup />} />
          <Route path="/become-creator" element={<BecomeCreator />} />
          <Route path="/games"          element={<Games />} />
          <Route path="/games/leaderboard" element={<WordDropLeaderboard />} />
          <Route path="/games/number-drop/leaderboard" element={<NumberDropLeaderboard />} />

          {/* ── MAIN APP ── */}
          <Route path="/gallery"           element={<SignedInOnly><Discover /></SignedInOnly>} />
          <Route path="/gallery/fresh"     element={<SignedInOnly><FreshMomentsPage /></SignedInOnly>} />
          <Route path="/gallery/rising"    element={<SignedInOnly><RisingPage /></SignedInOnly>} />
          <Route path="/discover"          element={<Navigate to="/gallery" replace />} />
          <Route path="/featured"          element={<SignedInOnly><FeaturedLanding /></SignedInOnly>} />
          <Route path="/featured/discovery" element={<SignedInOnly><FeaturedDiscoveryPage /></SignedInOnly>} />
          <Route path="/featured/community" element={<SignedInOnly><FeaturedCommunityPage /></SignedInOnly>} />
          <Route path="/following"         element={<SignedInOnly><FollowingFeed /></SignedInOnly>} />
          <Route path="/search"            element={<Search />} />
          <Route path="/post"              element={<EmailVerificationGate><PostADrop /></EmailVerificationGate>} />
          <Route path="/post/:id"          element={<PostToDropRedirect />} />
          <Route path="/drop/:id"          element={<DropView />} />
          <Route path="/creator/broadcasts" element={<Navigate to="/broadcasts" replace />} />
          <Route path="/creator/:handle"   element={<CreatorProfile />} />
          <Route path="/creator/:handle/edit" element={<ProfileSettings />} />
          <Route path="/profile"           element={<OwnUserProfile />} />
          <Route path="/profile/edit"      element={<UserProfileEdit />} />
          <Route path="/user/:userId"      element={<OtherUserProfile />} />
          <Route path="/fan/:userId"       element={<FanProfile />} />
          <Route path="/fan/:userId/edit"  element={<FanProfileEdit />} />
          <Route path="/profile/:username" element={<ProfileUsernameRedirect />} />
          <Route path="/notifications"     element={<SignedInOnly><Notifications /></SignedInOnly>} />
          <Route path="/settings"          element={<SettingsPage />} />
          <Route path="/perks"             element={<SignedInOnly><Perks /></SignedInOnly>} />
          <Route path="/themes"            element={<EmailVerificationGate><ThemeCatalog /></EmailVerificationGate>} />

          {/* ── INFO & LEGAL ── */}
          <Route path="/referral"       element={<Referral />} />
          <Route path="/help"           element={<Help />} />
          <Route path="/legal"          element={<Legal />} />
          <Route path="/terms"          element={<TermsOfServicePage />} />
          <Route path="/privacy"        element={<PrivacyPolicy />} />
          <Route path="/unsubscribe"    element={<Unsubscribe />} />
          <Route path="/admin-reports"  element={<AdminGate><AdminReports /></AdminGate>} />
          <Route path="/beta-checklist" element={<BetaChecklist />} />

          {/* ── ADMIN (superadmin only) ── */}
          <Route path="/admin" element={<AdminGate><AdminHome /></AdminGate>} />
          <Route path="/admin/approvals/creators" element={<AdminGate><AdminCreatorApprovals /></AdminGate>} />
          <Route path="/admin/approvals/fans" element={<AdminGate><AdminFanWaitlist /></AdminGate>} />
          <Route path="/admin/support" element={<AdminGate><AdminSupportInbox /></AdminGate>} />
          <Route path="/admin/games" element={<AdminGate><AdminGames /></AdminGate>} />
          <Route path="/admin/comment-reports" element={<AdminGate><AdminCommentReports /></AdminGate>} />
          <Route path="/admin/suggestions" element={<AdminGate><AdminSuggestions /></AdminGate>} />
          <Route path="/admin/email-templates" element={<AdminGate><AdminEmailTemplates /></AdminGate>} />
          <Route path="/admin/outreach" element={<AdminGate><AdminOutreach /></AdminGate>} />
          <Route path="/admin/users" element={<AdminGate><AdminUsers /></AdminGate>} />
          <Route path="/admin/settings" element={<AdminGate><AdminSettings /></AdminGate>} />
          {/* Legacy admin path redirects */}
          <Route path="/admin/creators" element={<Navigate to="/admin/approvals/creators" replace />} />
          <Route path="/admin/fans-waitlist" element={<Navigate to="/admin/approvals/fans" replace />} />
          <Route path="/admin/emails" element={<Navigate to="/admin/email-templates" replace />} />

          {/* ── CREATOR ONLY ── */}
          <Route path="/new-post"           element={<Navigate to="/post" replace />} />
          <Route path="/dashboard"          element={<EmailVerificationGate><CreatorOnly><Dashboard /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/my-drops"            element={<EmailVerificationGate><CreatorOnly><MyDrops /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/drops"              element={<EmailVerificationGate><CreatorOnly><MyDrops /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/creator-onboarding" element={<EmailVerificationGate><CreatorOnly><CreatorOnboarding /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/schedule-drop"      element={<EmailVerificationGate><CreatorOnly><ScheduleDrop /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/support-tiers"      element={<EmailVerificationGate><CreatorOnly><SubscriptionTiers /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/broadcasts"         element={<EmailVerificationGate><CreatorOnly><Broadcasts /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/earnings"           element={<EmailVerificationGate><CreatorOnly><Earnings /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/subscribers"        element={<EmailVerificationGate><CreatorOnly><Subscribers /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/analytics"          element={<EmailVerificationGate><CreatorOnly><Analytics /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/appreciation-wall" element={<EmailVerificationGate><CreatorOnly><AppreciationWall /></CreatorOnly></EmailVerificationGate>} />
          <Route path="/wall"              element={<EmailVerificationGate><CreatorOnly><AppreciationWall /></CreatorOnly></EmailVerificationGate>} />

          <Route path="/pip-masters"    element={<PipMasters />} />

          {/* ── CATCH-ALL ── */}
          <Route path="*"                element={<NotFound />} />

        </Routes>
      </main>

      {/* ── GLOBAL COMPONENTS ── */}
      {showAppShell && (
        <AddToHomeScreen onVisibilityChange={setA2hsBlocking} />
      )}
      {showAppShell && <PipEgg />}
      {showAppShell && !a2hsBlocking && <PipOnboarding />}
      {showAppShell && <SuggestionBox />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ReferralOverlayProvider>
        <PipProvider>
          <AppLayout />
        </PipProvider>
      </ReferralOverlayProvider>
    </BrowserRouter>
  );
}

export default App;